export const CHUNK_RETRY_DEFAULTS = Object.freeze({
  retries: 3,
  baseDelayMs: 400,
  maxDelayMs: 4000,
});

// A dynamic import can fail in three distinct shapes depending on who is
// fetching what, and the app-to-app return has produced all three: webpack
// raises a `ChunkLoadError` for its own chunks, Module Federation raises a
// plain Error while fetching a remote container, and the browser raises a
// TypeError with a vendor-specific message for native module scripts. Matching
// only `ChunkLoadError` would leave the remote and the native paths unretried.
const CHUNK_ERROR_NAMES = Object.freeze(['ChunkLoadError']);

const CHUNK_ERROR_PATTERNS = Object.freeze([
  /loading chunk \S+ failed/i,
  /loading css chunk/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /loading script failed/i,
  /script .*(load|fetch) failed/i,
  /remoteentry/i,
  /is not a valid javascript mime type/i,
  /networkerror when attempting to fetch resource/i,
  /failed to fetch/i,
]);

export const isChunkLoadError = (error) => {
  if (!error) return false;

  if (CHUNK_ERROR_NAMES.includes(error.name)) return true;

  const message = typeof error === 'string' ? error : error.message;
  if (typeof message !== 'string' || message === '') return false;

  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

// Full jitter: every client that lost the same packet would otherwise come back
// in lockstep. `attempt` is zero-based, so the first retry waits ~baseDelayMs.
export const computeBackoffDelay = ({
  attempt,
  baseDelayMs = CHUNK_RETRY_DEFAULTS.baseDelayMs,
  maxDelayMs = CHUNK_RETRY_DEFAULTS.maxDelayMs,
  random = Math.random,
} = {}) => {
  const exponential = Math.min(maxDelayMs, baseDelayMs * 2 ** attempt);
  return Math.round(exponential / 2 + random() * (exponential / 2));
};

export const importWithRetry = (importer, options = {}) => {
  const {
    retries = CHUNK_RETRY_DEFAULTS.retries,
    baseDelayMs = CHUNK_RETRY_DEFAULTS.baseDelayMs,
    maxDelayMs = CHUNK_RETRY_DEFAULTS.maxDelayMs,
    retryOnlyChunkErrors = true,
    onRetry,
    deps = {},
  } = options;

  const _setTimeout = deps.setTimeout || ((fn, ms) => setTimeout(fn, ms));
  const _random = deps.random || Math.random;

  const run = (attempt) =>
    Promise.resolve()
      .then(() => importer())
      .catch((error) => {
        const isRetryable = !retryOnlyChunkErrors || isChunkLoadError(error);

        if (!isRetryable || attempt >= retries) throw error;

        const delayMs = computeBackoffDelay({
          attempt,
          baseDelayMs,
          maxDelayMs,
          random: _random,
        });

        if (typeof onRetry === 'function') {
          onRetry({ attempt: attempt + 1, retries, delayMs, error });
        }

        return new Promise((resolve, reject) => {
          _setTimeout(() => {
            run(attempt + 1).then(resolve, reject);
          }, delayMs);
        });
      });

  return run(0);
};
